import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface DragState {
  isDragging: boolean;
  draggedItemId: string | null;
  draggedItemType: 'position' | 'sl' | 'tp' | null;
  startPosition: { x: number; y: number } | null;
  currentPosition: { x: number; y: number } | null;
  originalData: any | null;
}

export interface DropZone {
  id: string;
  type: 'price' | 'position';
  price?: number;
  bounds: { x: number; y: number; width: number; height: number };
}

interface DragDropContextType {
  dragState: DragState;
  dropZones: DropZone[];
  startDrag: (itemId: string, itemType: 'position' | 'sl' | 'tp', startPos: { x: number; y: number }, originalData: any) => void;
  updateDrag: (currentPos: { x: number; y: number }) => void;
  endDrag: () => void;
  addDropZone: (zone: DropZone) => void;
  removeDropZone: (zoneId: string) => void;
  getDropZoneAt: (x: number, y: number) => DropZone | null;
  isConfirming: boolean;
  setConfirming: (confirming: boolean) => void;
  pendingAction: {
    type: 'move_position' | 'update_sl' | 'update_tp';
    positionId: string;
    newPrice?: number;
    newPosition?: { x: number; y: number };
    startPosition?: { x: number; y: number };
  } | null;
  setPendingAction: (action: any) => void;
  confirmAction: () => void;
  cancelAction: () => void;
}

const DragDropContext = createContext<DragDropContextType | undefined>(undefined);

export const useDragDropContext = () => {
  const context = useContext(DragDropContext);
  if (context === undefined) {
    throw new Error('useDragDropContext must be used within a DragDropProvider');
  }
  return context;
};

interface DragDropProviderProps {
  children: ReactNode;
}

export const DragDropProvider: React.FC<DragDropProviderProps> = ({ children }) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItemId: null,
    draggedItemType: null,
    startPosition: null,
    currentPosition: null,
    originalData: null,
  });

  const [dropZones, setDropZones] = useState<DropZone[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pendingAction, setPendingAction] = useState<any>(null);

  const startDrag = useCallback((
    itemId: string, 
    itemType: 'position' | 'sl' | 'tp', 
    startPos: { x: number; y: number }, 
    originalData: any
  ) => {
    setDragState({
      isDragging: true,
      draggedItemId: itemId,
      draggedItemType: itemType,
      startPosition: startPos,
      currentPosition: startPos,
      originalData,
    });
  }, []);

  const updateDrag = useCallback((currentPos: { x: number; y: number }) => {
    setDragState(prev => ({
      ...prev,
      currentPosition: currentPos,
    }));
  }, []);

  const endDrag = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedItemId: null,
      draggedItemType: null,
      startPosition: null,
      currentPosition: null,
      originalData: null,
    });
  }, []);

  const addDropZone = useCallback((zone: DropZone) => {
    setDropZones(prev => [...prev.filter(z => z.id !== zone.id), zone]);
  }, []);

  const removeDropZone = useCallback((zoneId: string) => {
    setDropZones(prev => prev.filter(z => z.id !== zoneId));
  }, []);

  const getDropZoneAt = useCallback((x: number, y: number) => {
    return dropZones.find(zone => 
      x >= zone.bounds.x && 
      x <= zone.bounds.x + zone.bounds.width &&
      y >= zone.bounds.y && 
      y <= zone.bounds.y + zone.bounds.height
    ) || null;
  }, [dropZones]);

  const setConfirming = useCallback((confirming: boolean) => {
    setIsConfirming(confirming);
  }, []);

  const confirmAction = useCallback(() => {
    // This will be handled by the component that uses the context
    setPendingAction(null);
    setIsConfirming(false);
  }, []);

  const cancelAction = useCallback(() => {
    setPendingAction(null);
    setIsConfirming(false);
  }, []);

  const value: DragDropContextType = {
    dragState,
    dropZones,
    startDrag,
    updateDrag,
    endDrag,
    addDropZone,
    removeDropZone,
    getDropZoneAt,
    isConfirming,
    setConfirming,
    pendingAction,
    setPendingAction,
    confirmAction,
    cancelAction,
  };

  return (
    <DragDropContext.Provider value={value}>
      {children}
    </DragDropContext.Provider>
  );
};
